import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Reader from "@/components/Reader";
import VerseSheet from "@/components/VerseSheet";
import WordDetail from "@/components/WordDetail";
import ChapterPicker from "@/components/ChapterPicker";
import ChapterSidebar from "@/components/ChapterSidebar";
import SearchBar from "@/components/SearchBar";
import SelectionActionBar from "@/components/SelectionActionBar";
import SelectionHint from "@/components/SelectionHint";
import SelectionPresets from "@/components/SelectionPresets";
import QuickHighlightStrip from "@/components/QuickHighlightStrip";
import QuestionSheet from "@/components/QuestionSheet";
import TodayGoalStrip from "@/components/TodayGoalStrip";
import useMediaQuery from "@/hooks/useMediaQuery";
import { fetchChapter, fetchChapters, listHighlights, fetchQuestionsForChapter, createHighlight } from "@/lib/api";

const STORAGE_KEY = "matthew-reader-position";
const VERSION_KEY = "matthew-reader-version";

export default function ReaderPage() {
    const isDesktop = useMediaQuery("(min-width: 1024px)");
    const [sp, setSp] = useSearchParams();
    const nav = useNavigate();

    const [chapters, setChapters] = useState([]);
    const [chapter, setChapter] = useState(() => {
        const qCh = Number(sp.get("chapter"));
        if (qCh) return qCh;
        const saved = localStorage.getItem(STORAGE_KEY);
        return saved ? Number(JSON.parse(saved).chapter) || 1 : 1;
    });
    const [version, setVersion] = useState(() =>
        (localStorage.getItem(VERSION_KEY) || "kjv").toLowerCase()
    );
    const [verses, setVerses] = useState([]);
    const [activeVerse, setActiveVerse] = useState(() => Number(sp.get("verse")) || null);
    const [openVerse, setOpenVerse] = useState(null);
    const [openToken, setOpenToken] = useState(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    // Selection & highlights
    const [selecting, setSelecting] = useState(false);
    const [selectedVerses, setSelectedVerses] = useState([]);
    const [highlights, setHighlights] = useState([]);
    const [toast, setToast] = useState("");

    // Quick-highlight gesture (long-press + swipe)
    const [quickHL, setQuickHL] = useState(null); // { verse, x, y } | null

    // QuestionGod
    const [questionsByVerse, setQuestionsByVerse] = useState({});
    const [openQuestionVerse, setOpenQuestionVerse] = useState(null);
    const [showQuestions, setShowQuestions] = useState(() => {
        try { return localStorage.getItem("show-questions") !== "0"; } catch { return true; }
    });
    useEffect(() => {
        try { localStorage.setItem("show-questions", showQuestions ? "1" : "0"); } catch (_e) { /* ignore */ }
    }, [showQuestions]);
    useEffect(() => {
        fetchQuestionsForChapter("Mat", chapter)
            .then(d => setQuestionsByVerse(d.verses || {}))
            .catch(() => setQuestionsByVerse({}));
    }, [chapter]);

    useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify({ chapter })); }, [chapter]);
    useEffect(() => { localStorage.setItem(VERSION_KEY, version); }, [version]);

    useEffect(() => { fetchChapters().then(setChapters).catch(() => setChapters([])); }, []);

    const loadHighlights = useCallback(() => {
        listHighlights({ book: "Mat" }).then(d => setHighlights(d.highlights || []));
    }, []);
    useEffect(() => { loadHighlights(); }, [loadHighlights, chapter]);

    useEffect(() => {
        setLoading(true);
        fetchChapter(chapter)
            .then(d => setVerses(d.verses))
            .catch(() => setVerses([]))
            .finally(() => setLoading(false));
        setSelecting(false); setSelectedVerses([]);
    }, [chapter]);

    // Deep-link: on chapter load, scroll to verse if given
    useEffect(() => {
        if (!verses.length) return;
        const v = Number(sp.get("verse"));
        if (v) {
            setTimeout(() => {
                const el = document.querySelector(`[data-testid="verse-${v}"]`);
                el?.scrollIntoView({ behavior: "smooth", block: "center" });
                setActiveVerse(v);
                const next = new URLSearchParams(sp);
                next.delete("verse");
                setSp(next, { replace: true });
            }, 250);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [verses]);

    // Toast auto-dismiss
    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(""), 2000);
        return () => clearTimeout(t);
    }, [toast]);

    const onTapVerse = useCallback((v) => {
        if (selecting) return;
        setActiveVerse(v);
        setOpenQuestionVerse(null);
        setOpenVerse({ chapter, verse: v });
    }, [chapter, selecting]);

    const onLongPressVerse = useCallback((v, anchor) => {
        setSelecting(true);
        setSelectedVerses(prev => prev.includes(v) ? prev : [...prev, v]);
        if (anchor && Number.isFinite(anchor.x) && Number.isFinite(anchor.y)) {
            setQuickHL({ verse: v, x: anchor.x, y: anchor.y });
        }
    }, []);

    const applyQuickHighlight = useCallback(async (color) => {
        const verse = quickHL?.verse;
        setQuickHL(null);
        if (!verse) return;
        try {
            await createHighlight([`Mat.${chapter}.${verse}`], color);
            setToast(`Highlighted verse ${verse}`);
            setSelecting(false);
            setSelectedVerses([]);
            loadHighlights();
        } catch (_e) {
            setToast("Highlight failed");
        }
    }, [chapter, quickHL, loadHighlights]);

    const selectWholeChapter = useCallback(() => {
        const all = verses.map(v => v.verse);
        setSelecting(true);
        setSelectedVerses(all);
        setOpenVerse(null); setOpenToken(null); setOpenQuestionVerse(null); setActiveVerse(null);
    }, [verses]);

    const onToggleSelect = useCallback((v) => {
        setSelectedVerses(prev => {
            const next = prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v];
            if (next.length === 0) setSelecting(false);
            return next;
        });
    }, []);

    const exitSelection = useCallback(({ reload } = {}) => {
        setSelecting(false); setSelectedVerses([]);
        if (reload) loadHighlights();
    }, [loadHighlights]);

    // Compute highlights per verse for THIS chapter
    const highlightsByVerse = {};
    for (const h of highlights) {
        for (const r of h.refs || []) {
            const parts = r.split(".");
            if (parts[0] === "Mat" && Number(parts[1]) === chapter) {
                highlightsByVerse[Number(parts[2])] = h.color;
            }
        }
    }
    // Selected verses in this chapter, do any share an existing highlight?
    const hasHighlightOnSelection = selectedVerses.some(v => !!highlightsByVerse[v]);

    const jumpTo = useCallback((ch, v) => {
        setOpenToken(null);
        setChapter(ch);
        setActiveVerse(v);
        setOpenVerse({ chapter: ch, verse: v });
        setTimeout(() => {
            const el = document.querySelector(`[data-testid="verse-${v}"]`);
            el?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 300);
    }, []);

    const openHeaderPicker = () => { if (!isDesktop) setPickerOpen(true); };
    const showStudyPanel = isDesktop && (openVerse !== null || openToken !== null || openQuestionVerse !== null) && !selecting;

    return (
        <>
            <ChapterSidebar
                chapters={chapters}
                current={chapter}
                onSelect={(c) => { setChapter(c); setActiveVerse(null); setOpenVerse(null); setOpenToken(null); }}
            />

            <div
                data-testid="reader-area"
                className={
                    isDesktop
                        ? "grid h-screen overflow-hidden transition-[grid-template-columns] duration-300 " +
                          (showStudyPanel
                              ? "grid-cols-[1fr_min(360px,38vw)] xl:grid-cols-[1fr_min(460px,34vw)]"
                              : "grid-cols-[1fr_0px]")
                        : ""
                }
            >
                <div className={isDesktop ? "h-screen border-r border-ink-10 overflow-y-auto" : ""}>
                    <Header
                        chapter={chapter}
                        onOpenPicker={openHeaderPicker}
                        onOpenSearch={() => setSearchOpen(s => !s)}
                        searchOpen={searchOpen}
                        version={version}
                        onVersionChange={setVersion}
                        selectMode={selecting}
                        selectionCount={selectedVerses.length}
                        showQuestions={showQuestions}
                        onToggleShowQuestions={() => setShowQuestions(s => !s)}
                        onToggleSelectMode={() => {
                            if (selecting) { setSelecting(false); setSelectedVerses([]); }
                            else { setSelecting(true); setOpenVerse(null); setOpenToken(null); setOpenQuestionVerse(null); setActiveVerse(null); }
                        }}
                    />
                    {searchOpen && (
                        <SearchBar version={version} onJump={jumpTo} onClose={() => setSearchOpen(false)} />
                    )}
                    {!selecting && !searchOpen && <TodayGoalStrip />}
                    <SelectionHint selectMode={selecting} />
                    <SelectionPresets
                        selectMode={selecting}
                        verseCount={verses.length}
                        allSelected={verses.length > 0 && selectedVerses.length === verses.length}
                        selectionCount={selectedVerses.length}
                        onSelectWholeChapter={selectWholeChapter}
                        onClearAll={() => { setSelectedVerses([]); }}
                    />
                    {loading ? (
                        <div className="px-6 py-16 text-ink-40 font-ui text-sm">Loading Matthew…</div>
                    ) : (
                        <Reader
                            chapter={chapter}
                            verses={verses}
                            version={version}
                            onTapVerse={onTapVerse}
                            activeVerse={activeVerse}
                            showFooter={!isDesktop}
                            highlightsByVerse={highlightsByVerse}
                            selectionActive={selecting}
                            selectedVerses={selectedVerses}
                            onLongPressVerse={onLongPressVerse}
                            onToggleSelect={onToggleSelect}
                            questionsByVerse={questionsByVerse}
                            showQuestions={showQuestions}
                            onOpenQuestion={(v) => { setOpenQuestionVerse(v); setOpenVerse(null); setOpenToken(null); }}
                        />
                    )}
                </div>

                {isDesktop && (
                    <aside
                        data-testid="study-panel"
                        className={
                            "h-screen bg-white transition-transform duration-300 overflow-hidden " +
                            (showStudyPanel ? "translate-x-0" : "translate-x-full")
                        }
                    >
                        {openQuestionVerse && (
                            <QuestionSheet
                                variant="panel"
                                book="Mat" chapter={chapter} verse={openQuestionVerse}
                                onClose={() => setOpenQuestionVerse(null)}
                            />
                        )}
                        {!openQuestionVerse && openVerse && !openToken && (
                            <VerseSheet variant="panel" chapter={openVerse.chapter} verse={openVerse.verse}
                                onClose={() => { setOpenVerse(null); setActiveVerse(null); }}
                                onOpenWord={(t) => setOpenToken(t)} />
                        )}
                        {!openQuestionVerse && openToken && (
                            <WordDetail variant="panel" token={openToken}
                                onBack={() => setOpenToken(null)}
                                onClose={() => { setOpenToken(null); setOpenVerse(null); setActiveVerse(null); }}
                                onJumpVerse={jumpTo} />
                        )}
                    </aside>
                )}
            </div>

            {!isDesktop && pickerOpen && chapters.length > 0 && (
                <ChapterPicker chapters={chapters} current={chapter}
                    onSelect={(c) => { setChapter(c); setActiveVerse(null); }}
                    onClose={() => setPickerOpen(false)} />
            )}
            {!isDesktop && openQuestionVerse && (
                <QuestionSheet
                    book="Mat" chapter={chapter} verse={openQuestionVerse}
                    onClose={() => setOpenQuestionVerse(null)}
                />
            )}
            {!isDesktop && !selecting && openVerse && !openToken && (
                <VerseSheet chapter={openVerse.chapter} verse={openVerse.verse}
                    onClose={() => { setOpenVerse(null); setActiveVerse(null); }}
                    onOpenWord={(t) => setOpenToken(t)} />
            )}
            {!isDesktop && !selecting && openToken && (
                <WordDetail token={openToken}
                    onBack={() => setOpenToken(null)}
                    onClose={() => { setOpenToken(null); setOpenVerse(null); setActiveVerse(null); }}
                    onJumpVerse={jumpTo} />
            )}

            {selecting && selectedVerses.length > 0 && (
                <SelectionActionBar
                    chapter={chapter}
                    selectedVerses={selectedVerses}
                    hasHighlight={hasHighlightOnSelection}
                    version={version}
                    onDone={exitSelection}
                    onNavigateNote={(url) => { exitSelection(); nav(url); }}
                    onToast={setToast}
                />
            )}

            {toast && (
                <div data-testid="toast" className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-full bg-ink text-white font-ui text-[12px] shadow-lg animate-fade-in">
                    {toast}
                </div>
            )}

            {quickHL && (
                <QuickHighlightStrip
                    anchor={{ x: quickHL.x, y: quickHL.y }}
                    onPick={applyQuickHighlight}
                    onDismiss={() => setQuickHL(null)}
                />
            )}
        </>
    );
}
